import { useFeatureFlags } from "@hooks/http";
import { type User } from "@internal/user";
import {
  type BudgetMixBreakdownSpendDatum,
  formatting,
} from "@north-beam/nb-common";

import { type FormattedBreakdownTableRow } from "./table";

const getTotalSpend = (tableData: BudgetMixBreakdownSpendDatum[]) => {
  return tableData.map((d) => d.spend).reduce((a, b) => a + b);
};

export function useMMMAdminMode(user: User) {
  const { hasFeatureFlag } = useFeatureFlags();

  return user.isAdmin && hasFeatureFlag("mmmAdminMode");
}

const getSpendByBreakdown = (
  tableData: BudgetMixBreakdownSpendDatum[],
): Record<string, number> => {
  return tableData.reduce(
    (agg: Record<string, number>, datum: BudgetMixBreakdownSpendDatum) => {
      const breakdownValue = datum.breakdowns[0].value;
      if (!agg[breakdownValue]) {
        agg[breakdownValue] = datum.spend;
      } else {
        agg[breakdownValue] += datum.spend;
      }
      return agg;
    },
    {},
  );
};

const formatMoney = (num: number, user: User) =>
  formatting.formatCurrency(num, {
    precision: "exact",
    code: user.currencies.currentCurrency,
  });

const formatPercentage = (num: number) => {
  if (Number.isNaN(num)) {
    return "-%";
  }
  return formatting.formatPercentage(num, {
    precision: "exact",
  });
};

const getFormattedRows = (
  tableData: BudgetMixBreakdownSpendDatum[],
  spendByBreakdown: Record<string, number>,
  totalSpend: number,
  user: User,
): FormattedBreakdownTableRow[] => {
  // NOTE: Using Map to maintain insertion order which is necessary
  const rowReducer = (
    rowAggregate: Map<string, FormattedBreakdownTableRow>,
    tableDatum: BudgetMixBreakdownSpendDatum,
  ) => {
    const breakdownValue = tableDatum.breakdowns[0].value;
    if (!rowAggregate.get(breakdownValue)) {
      rowAggregate.set(breakdownValue, {
        ...tableDatum,
        spend: formatMoney(spendByBreakdown[breakdownValue], user),
        mix: formatPercentage(spendByBreakdown[breakdownValue] / totalSpend),
        label: breakdownValue,
        items: [
          {
            ...tableDatum,
            spend: formatMoney(tableDatum.spend, user),
            mix: formatPercentage(tableDatum.spend / totalSpend),
            label: tableDatum.breakdowns[1]?.value ?? "",
          },
        ],
      } as FormattedBreakdownTableRow);
    } else {
      rowAggregate.get(breakdownValue)?.items?.push({
        ...tableDatum,
        spend: formatMoney(tableDatum.spend, user),
        mix: formatPercentage(tableDatum.spend / totalSpend),
        label: tableDatum.breakdowns[1]?.value ?? "",
      } as FormattedBreakdownTableRow);
    }

    return rowAggregate;
  };
  return Array.from(
    tableData
      .reduce(rowReducer, new Map() as Map<string, FormattedBreakdownTableRow>)
      .values(),
  );
};

export const getRows = (
  tableData: BudgetMixBreakdownSpendDatum[],
  user: User,
): FormattedBreakdownTableRow[] => {
  const totalSpend = getTotalSpend(tableData);
  const spendByBreakdown = getSpendByBreakdown(tableData);
  return getFormattedRows(tableData, spendByBreakdown, totalSpend, user);
};

export const getChildRows = (
  row: FormattedBreakdownTableRow,
  rootRows: FormattedBreakdownTableRow[],
): FormattedBreakdownTableRow[] | null => (row ? row?.items : rootRows) || null;
